import { styled } from "@pigment-css/react";
import { ChevronLeft } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import type { Customer } from "@acto/taskpane/core/domain";
import { useCustomers } from "@acto/taskpane/core/data-access/customers/useCustomers";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";
import { useInfiniteScroll } from "@acto/taskpane/field-sales-2/pages/useInfiniteScroll";
import { FullScreenSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { useAutoFocusNoScroll } from "@acto/taskpane/field-sales-2/components/primitives/useAutoFocusNoScroll";
import { Skeleton } from "@acto/taskpane/field-sales-2/components/primitives/Skeleton";

const Content = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
}));

const SearchInput = styled("input")(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  height: theme.fieldSalesTwo.spacing[50],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius[32],
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  background: theme.fieldSalesTwo.colors.surface.glass,
  outline: "none",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": { color: theme.fieldSalesTwo.colors.neutral[700] },
}));

const List = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  overflow: "hidden",
  "& > :not(:last-child)": {
    borderBottom: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
  },
}));

const Row = styled("button")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[2],
  width: "100%",
  textAlign: "left",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[20]}`,
  WebkitTapHighlightColor: "transparent",
  transition: "background 120ms ease",
  "&:hover": { background: theme.fieldSalesTwo.colors.surface.glassMedium },
  "&:active": { background: theme.fieldSalesTwo.colors.surface.glassStrong },
}));

// Customer ID sits above the name (per design decision for this picker).
const RowId = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const RowName = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Message = styled("p")(({ theme }) => ({
  margin: 0,
  padding: theme.fieldSalesTwo.spacing[24],
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  color: theme.fieldSalesTwo.colors.neutral[600],
}));

// Trailing element the IntersectionObserver watches to page in the next batch of customers.
const Sentinel = styled("div")({
  height: "1px",
});

// Placeholder row (ID line + name line) matching the Row layout, shown while customers load.
const SkeletonRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[6],
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[20]}`,
}));

function CustomerRowSkeleton() {
  return (
    <SkeletonRow aria-hidden="true">
      <Skeleton.Line width="30%" height="0.8125rem" />
      <Skeleton.Line width="60%" height="0.9375rem" />
    </SkeletonRow>
  );
}

const INITIAL_SKELETON_COUNT = 6;
const MORE_SKELETON_COUNT = 3;

interface CustomerPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (customer: Customer) => void;
}

export function CustomerPickerSheet({ isOpen, onClose, onSelect }: CustomerPickerSheetProps) {
  const t = useTranslations("fieldSalesTwo.todoCreate");
  const tSearch = useTranslations("fieldSalesTwo.search");
  const searchRef = useAutoFocusNoScroll<HTMLInputElement>();
  const { displayValue, debouncedValue, setDisplayValue } = useDebouncedSearchTerm();

  const {
    data: customers,
    isPending,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useCustomers({
    searchTerm: debouncedValue || undefined,
    limit: 20,
    enabled: isOpen,
  });

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    hasMore: hasNextPage,
    isLoadingMore: isFetching,
    loadMore: fetchNextPage,
    resetKey: customers?.length,
  });

  return (
    <FullScreenSheet
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t("pickerTitle")}
      closeAriaLabel={t("closeAriaLabel")}
      closeIcon={<ChevronLeft />}
    >
      <Content>
        <SearchInput
          ref={searchRef}
          type="text"
          value={displayValue}
          onChange={(event) => setDisplayValue(event.target.value)}
          placeholder={t("pickerSearchPlaceholder")}
          aria-label={t("pickerSearchPlaceholder")}
        />
        {customers && customers.length > 0 ? (
          <>
            <List>
              {customers.map((customer) => (
                <Row key={customer.id} type="button" onClick={() => onSelect(customer)}>
                  <RowId>{customer.externalReference}</RowId>
                  <RowName>{customer.name}</RowName>
                </Row>
              ))}
              {/* Loading more: shimmer rows where the next page will land. */}
              {isFetching &&
                Array.from({ length: MORE_SKELETON_COUNT }).map((_, i) => <CustomerRowSkeleton key={`more-${i}`} />)}
            </List>
            {hasNextPage && <Sentinel ref={sentinelRef} aria-hidden="true" />}
          </>
        ) : isPending ? (
          <List>
            {Array.from({ length: INITIAL_SKELETON_COUNT }).map((_, i) => (
              <CustomerRowSkeleton key={`init-${i}`} />
            ))}
          </List>
        ) : (
          <Message>{tSearch("noResults")}</Message>
        )}
      </Content>
    </FullScreenSheet>
  );
}
